import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BaseAppService } from '../base-app-service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/fence/configuration';
import { Installation } from 'src/app/models/configurators-2d/applications/fence/installations/installation';
import { Shape } from '../../../models/configurators-2d/applications/fence/shape';
import { Resume } from 'src/app/models/configurators-2d/resume/resume';
import { Board } from '../../../models/configurators-2d/applications/fence/board';
import { PostType } from '../../../models/configurators-2d/applications/fence/post-type';
import { Decoration } from '../../../models/configurators-2d/applications/fence/decoration';
import { Door } from '../../../models/configurators-2d/applications/fence/door';
import {
  InstallationMixing
} from '../../../models/configurators-2d/applications/fence/installations/installation-mixing';
import { KeyValue } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class FenceService extends BaseAppService<Configuration> {
  constructor() {
    super(CONFIG_CONTEXT.FENCE, Configuration);
  }

  getAccessories(locationId: number): Promise<any> {
    const params = new HttpParams().set('locationId', locationId);
    return this.get(this.apiUrl + 'Configuration/Accessories', params);
  }

  /**
   * Fonction de récupération des formes de terrasse
   * @returns Promise
   */
  getShapes(): Promise<Shape[]> {
    return this.get(this.apiUrl + 'Configuration/Shapes');
  }

  /**
   * Requête de récupération des types de lame
   * @param installationId
   * @param otherInstallationIds
   * @param heights
   * @param locationId
   * @param wallHeight
   * @param hasNonRightAngle
   */
  getBoards(
    installationId: number,
    otherInstallationIds: number[],
    heights: number[],
    locationId: number,
    wallHeight: number,
    hasNonRightAngle: boolean
  ): Promise<KeyValue<number, Board[]>> {
    let params = new HttpParams()
      .set('installationId', installationId)
      .set('locationId', locationId)
      .set('wallHeight', wallHeight)
      .set('hasNonRightAngle', hasNonRightAngle);

    for (const height of heights) {
      params = params.append('heights', height);
    }

    for (const otherInstallationId of otherInstallationIds) {
      params = params.append('otherInstallationIds', otherInstallationId);
    }

    const result = this.get<Board[]>(this.apiUrl + 'Configuration/BoardTypes', params);
    return new Promise(resolve => {
      result.then(boards => {
        resolve({ key: installationId, value: boards });
      });
    });
  }

  /**
   * Requête de récupération des types de poteau
   * @param heights
   * @param boardIds
   * @param locationId
   * @param wallHeight
   * @param hasNonRightAngle
   * @param hasAgainstWall
   * @param hasDoor
   * @param postTypeSelectedId
   */
  getPostTypes(
    heights: number[],
    boardIds: number[],
    locationId: number,
    wallHeight: number,
    hasNonRightAngle: boolean,
    hasAgainstWall: boolean,
    hasDoor: boolean,
    postTypeSelectedId?: number
  ): Promise<PostType[]> {
    let params = new HttpParams()
      .set('locationId', locationId)
      .set('wallHeight', wallHeight)
      .set('hasNonRightAngle', hasNonRightAngle)
      .set('hasAgainstWall', hasAgainstWall)
      .set('hasGate', hasDoor)
      .set('postTypeSelectedId', postTypeSelectedId);

    for (const height of heights) {
      params = params.append('heights', height);
    }

    for (const boardId of boardIds) {
      params = params.append('boardIds', boardId);
    }

    return this.get<PostType[]>(this.apiUrl + 'Configuration/PostTypes', params);
  }

  /**
   * Requête de récupération des décors
   * @param postTypeId
   * @param installationIds
   * @param maxHeight
   * @param idsSelected
   */
  getDecorations(
    postTypeId: number,
    installationIds: number[],
    maxHeight: number,
    idsSelected: number[]
  ): Promise<Decoration[]> {
    let params = new HttpParams()
      .set('postTypeId', postTypeId)
      .set('maxHeight', maxHeight);

    for (const idSelected of idsSelected) {
      params = params.append('idsSelected', idSelected);
    }

    for (const installationId of installationIds) {
      params = params.append('installationIds', installationId);
    }

    return this.get<Decoration[]>(this.apiUrl + 'Configuration/Decors', params);
  }

  /**
   * Requête de récupération des portillons
   * @param postTypeId
   * @param maxHeight
   * @param idsSelected
   */
  getDoors(postTypeId: number, maxHeight: number, idsSelected: number[]): Promise<Door[]> {
    let params = new HttpParams().set('postTypeId', postTypeId).set('maxHeight', maxHeight);

    for (const idSelected of idsSelected) {
      params = params.append('idsSelected', idSelected);
    }

    return this.get<Door[]>(this.apiUrl + 'Configuration/Gates', params);
  }

  /**
   * Sert à mettre à jour la configuration quand la référence de la variable a changé
   * @param localConfig
   */
  updateConfiguration(localConfig: Configuration): void {
    Object.assign(this.configuration, localConfig);
  }

  /**
   * Fonction de récupération du résumé en envoyant la configuration
   * @returns résumé
   */
  override getResume<R extends Resume>(): Promise<R> {
    let localConfig: Configuration = new Configuration();
    Object.assign(localConfig, this.configuration);
    localConfig.isComplete = true;

    localConfig.postType = {
      id: localConfig.postType.id,
      icon: localConfig.postType.icon,
      nameFr: localConfig.postType.nameFr,
      nameEn: localConfig.postType.nameEn,
      isCuttable: localConfig.postType.isCuttable,
      isGateCompatible: localConfig.postType.isGateCompatible,
      isDisabled: localConfig.postType.isDisabled,
      acceptAngle: localConfig.postType.acceptAngle,
    };

    return super.getResume<R>(localConfig);
  }

  protected override simplifyConfiguration(): Configuration {
    let localConfig: Configuration = new Configuration();
    Object.assign(localConfig, this.configuration);

    if (localConfig.postType) {
      localConfig.postType = {
        id: localConfig.postType.id,
        icon: localConfig.postType.icon,
        nameFr: localConfig.postType.nameFr,
        nameEn: localConfig.postType.nameEn,
        isCuttable: localConfig.postType.isCuttable,
        isGateCompatible: localConfig.postType.isGateCompatible,
        isDisabled: localConfig.postType.isDisabled,
        acceptAngle: localConfig.postType.acceptAngle,
      };
    }

    return localConfig;
  }

  getInstallations(): Promise<Installation[]> {
    return this.get<Installation[]>(this.apiUrl + 'Configuration/Installations');
  }

  getInstallationMixings(): Promise<InstallationMixing[]> {
    return this.get<InstallationMixing[]>(this.apiUrl + 'Configuration/InstallationMixings');
  }
}
